import React from 'react'
import { useSelector } from 'react-redux'

import { useWindowSize } from 'hooks/react.hooks'
import { mobileWidth } from 'enum/responsive-width.enum'
import { IMainState } from 'store/states'

export * from './sence'
export * from './determineGrammaticalGender'

export const useIsDesktop = () => {
  const windowsSize = useWindowSize()
  const onDesktop = React.useMemo(
    () => ((windowsSize?.width || 0) < mobileWidth ? false : true),
    [windowsSize.width]
  )
  return onDesktop
}

export const useBasicPermission = () => {
  const { permission } = useSelector(({ user }: IMainState) => user)

  return permission.admin || permission.superadmin
}

const fullscreenElementProp = getBrowserFullscreenElementProp()

export function useFullscreenStatus(
  elRef: React.RefObject<HTMLElement>
): [boolean, (state?: boolean) => void] {
  const [isFullscreen, setIsFullscreen] = React.useState(
    document[fullscreenElementProp] != null
  )

  const setFullscreen = (open = true) => {
    if (elRef.current == null) return
    const requestFullscreen = () => {
      if (elRef.current == null) return Promise.reject()
      return Promise.all([
        elRef.current.requestFullscreen(),
        elRef.current['webkitRequestFullscreen'](),
      ])
    }
    const exitFullscreen =
      document.exitFullscreen || document['webkitExitFullscreen']

    if (open) {
      requestFullscreen()
        .then(() => {
          setIsFullscreen(document[fullscreenElementProp] != null)
        })
        .catch(() => {
          setIsFullscreen(false)
        })
    } else {
      exitFullscreen.call(document)
      setIsFullscreen(false)
    }
  }

  React.useLayoutEffect(() => {
    document.onfullscreenchange = () =>
      setIsFullscreen(document[fullscreenElementProp] != null)

    return () => {
      document.onfullscreenchange = null
    }
  })

  return [isFullscreen, setFullscreen]
}

function getBrowserFullscreenElementProp() {
  const obj: any = document
  if (typeof obj.fullscreenElement !== 'undefined') {
    return 'fullscreenElement'
  } else if (typeof obj.mozFullScreenElement !== 'undefined') {
    return 'mozFullScreenElement'
  } else if (typeof obj.msFullscreenElement !== 'undefined') {
    return 'msFullscreenElement'
  } else if (typeof obj.webkitFullscreenElement !== 'undefined') {
    return 'webkitFullscreenElement'
  } else if (typeof obj.webkitCurrentFullScreenElement !== 'undefined') {
    return 'webkitCurrentFullScreenElement'
  } else {
    console.error('fullscreenElement is not supported by this browser')
    return ''
  }
}

export const hasProtocol = (url?: string) => !!url?.match(/^[a-z]+:\/\//i)

export const cleanUrl = (url?: string) => {
  return url?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '') || ''
}

export const saveAs = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  window.URL.revokeObjectURL(url)
}

export const getPercentage = (number: number, total: number) =>
  Math.round((number / (total || 1)) * 100)

export const buildQuery = (ids: number[], allSelected: boolean) => ({
  id_in: allSelected ? [] : ids.length ? ids : [0],
  ...(allSelected && { id_not_in: ids }),
})

export const removeAccents = (str: string) =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

export const canSkipOnRowSelected = (dispatcher: string) =>
  'handleUnselectAllRowsOnFilter' === dispatcher

export const isZipFile = (file: File) => {
  const fileName = file.name.toLowerCase()
  return fileName.endsWith('.zip')
}

export const isExcelFile = (file: File) => {
  const fileName = file.name.toLowerCase()
  return fileName.endsWith('.xlsx') || fileName.endsWith('.xls')
}

export const injectCss = (styleSheet: string) => {
  const styleElement = document.createElement('style')

  styleElement.innerHTML = styleSheet

  document.head.append(styleElement)
  return styleElement
}
